import type { INodeDto } from "@/types/INodeDto";
import { createAndSetStorage } from "@/types/internNodes";
import {
  materialsArrayToMap,
  readModelVersions,
  readMultipleModelData,
  serializeMap,
  supplementalsArrayToMap,
  versionsMapToObject,
  writeMultipleModelData
} from "@/types/storage/modelDataStorage";
import { readModelFile } from "@/types/storage/opfsStorage";
import {
  UpdateModelVersionRequest as ApiUpdateModelVersionRequest,
  AssignMaterialsRequest,
  AssignNodesMaterialRequest,
  AssignNonModelledElementsRequest,
  AssignRebarEstimationEditsRequest,
  AssignRebarEstimationsRequest,
  AssignScopesRequest,
  AutoAssignMaterialsRequest,
  
  CreateModelVersionRequest,
  DeleteModelRequest,
  Element,
  FileParameter,
  GetScenarioNodeDataBatchRequest,
  INodeMaterialDto,
  INodeSupplementalDto,
  Material,
  Model,
  ModelsClient,
  ModelScopesResponse,
  ModelSplitRequest,
  ModelTypeDto,
  ModelUsageInfo,
  NodeDto,
  NodeMaterialDto,
  NodesClient,
  NodeSupplementalDto,
  RemoveMaterialsRequest,
  RemoveSupplementalsRequest,
  ScenarioNodeDataDto,
  UpsertModel
} from "@api/apiClient";
import axios from "@api/axiosInstance";
import { queryClient } from "@hooks/useGlobalState";
import { createMultiModelDataHook, MultiModelDataResult, UseMultiModelDataOptions } from "@hooks/useMultiModelData";
import { CategoryDto } from "@memorypack/CategoryDto";
import { deserializeConcatenatedToInternedNodeDtos } from "@memorypack/deserializeInternedNodeDto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

export const modelsClient = new ModelsClient(undefined, axios);
export const nodesClient = new NodesClient(undefined, axios);

export const useModel = (modelId: string | undefined, options?: any) => {
  return useQuery<Model | null>({
    ...options,
    queryKey: ["model", modelId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModel(modelId) : new Promise((resolve) => resolve(null)),
  });
};

export const useModelNodes = (modelId: string | undefined, options?: any) => {
  return useQuery<INodeDto[] | null>({
    ...options,
    queryKey: ["modelNodes", modelId],
    initialData: null,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!modelId) return null;
      try {
        const buffer = await readModelFile(modelId);
        if (buffer) {
          createAndSetStorage();
          return deserializeConcatenatedToInternedNodeDtos(buffer);
        }
        return null;
      } catch (error) {
        console.error(`[useModelNodes] Failed to deserialize model ${modelId} from OPFS:`, error);
        return null;
      }
    },
  });
};

export const useCategories = (modelId: string | undefined, scenarioId: string | undefined, options?: any) => {
  return useQuery<CategoryDto[] | null>({
    ...options,
    queryKey: ["categories", modelId, scenarioId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModelCategories(modelId!) : new Promise((resolve) => resolve(null)),
  });
};

export const useElements = (modelId: string | undefined, scenarioId: string | undefined, options?: any) => {
  return useQuery<Element[] | null>({
    ...options,
    queryKey: ["elements", modelId, scenarioId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModelElements(modelId!, scenarioId) : new Promise((resolve) => resolve(null)),
  });
};

export const useComponents = (modelId: string | undefined, scenarioId: string | undefined, options?: any) => {
  return useQuery<Element[] | null>({
    ...options,
    queryKey: ["components", modelId, scenarioId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModelComponents(modelId!, scenarioId) : new Promise((resolve) => resolve(null)),
  });
};

export const useMaterials = (modelId: string | undefined, scenarioId: string | undefined, options?: any) => {
  return useQuery<Material[] | null>({
    ...options,
    queryKey: ["materials", modelId, scenarioId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModelMaterials(modelId!, scenarioId) : new Promise((resolve) => resolve(null)),
  });
};

export const useModelScopes = (modelId: string | undefined, options?: any) => {
  return useQuery<ModelScopesResponse | null>({
    ...options,
    queryKey: ["modelScopes", modelId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModelScopes(modelId!) : new Promise((resolve) => resolve(null)),
  });
};

export const useCreateModelImage = (modelId: string | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (fileParameter: FileParameter) => modelsClient.uploadModelImage(modelId!, fileParameter),
  });
};

export const useUpdateModel = (modelId: string | undefined, options?: any) => {
  return useMutation<Model>({
    ...options,
    mutationFn: (upsertModel: UpsertModel) => modelsClient.updateModel(modelId!, upsertModel),
    onSuccess: async (model: Model) => {
      queryClient.setQueryData(["model", modelId], model);

      options?.onSuccess?.(model);
    },
  });
};

export const useAssignMaterials = (options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (assignMaterialsRequest: AssignMaterialsRequest) => modelsClient.assignMaterials(assignMaterialsRequest),
  });
};

export const useAutoAssignMaterials = (options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (autoAssignMaterialsRequest: AutoAssignMaterialsRequest) => modelsClient.autoAssignMaterials(autoAssignMaterialsRequest),
  });
};

export const useAssignRebarEstimations = (options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (assignRebarEstimationsRequest: AssignRebarEstimationsRequest) => modelsClient.assignRebarEstimations(assignRebarEstimationsRequest),
  });
};

export const useAssignPopulateModel = (modleId: string | null | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: () => modelsClient.autoPopulateModel(modleId!),
  });
};
export const useAssignNonModelledElements = (options?: any) => {


  return useMutation<void>({
    ...options,
    mutationFn: (assignNonModelledElementsRequest: AssignNonModelledElementsRequest) => modelsClient.assignNonModelledElements(assignNonModelledElementsRequest),
  });
};
export const useAssignRebarEstimationEdits = (options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (assignRebarEstimationEditsRequest: AssignRebarEstimationEditsRequest) => modelsClient.assignRebarEstimationEdits(assignRebarEstimationEditsRequest),
  });
};
export const useAssignScopes = (modleId: string | null | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (assignScopesRequest: AssignScopesRequest) => modelsClient.assignScopes(modleId!, assignScopesRequest),
  });
};

export const useRemoveMaterials = (options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (removeMaterialsRequest: RemoveMaterialsRequest) => modelsClient.removeMaterials(removeMaterialsRequest),
  });
};

export const useRemoveSupplementals = (options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (removeSupplementalsRequest: RemoveSupplementalsRequest) => modelsClient.removeSupplementals(removeSupplementalsRequest),
  });
};

/**
 * Hook for upserting node materials using the client-side calculated values.
 * Supports Insert, Update, and Delete operations via the AssignNodesMaterialRequest.
 */
export const useUpsertNodeMaterials = (options?: any) => {
  return useMutation<void, unknown, AssignNodesMaterialRequest>({
    ...options,
    mutationFn: (request: AssignNodesMaterialRequest) => nodesClient.assignNodesMaterials(request),
  });
};

export const useSplitModel = (options?: any) => {
  return useMutation<ModelSplitRequest, unknown, ModelSplitRequest>({
    ...options,
    mutationFn: (value: ModelSplitRequest) => modelsClient.splitModel(value),
  });
};

export const useCreateModelVersion = (options?: any) => {
  return useMutation<void, unknown, CreateModelVersionRequest>({
    ...options,
    mutationFn: (request: CreateModelVersionRequest) => modelsClient.createModelVersion(request),
  });
};

export const useDeleteModel = (options?: any) => {
  return useMutation<void, unknown, DeleteModelRequest>({
    ...options,
    mutationFn: (request: DeleteModelRequest) => modelsClient.deleteModel(request),
  });
};

export const useModelUsageInfo = (modelId: string | undefined, options?: any) => {
  return useQuery<ModelUsageInfo | null>({
    ...options,
    queryKey: ["modelUsageInfo", modelId],
    initialData: null,
    enabled: !!modelId,
    queryFn: async () => {
      if (!modelId) return null;
      return modelsClient.getModelUsageInfo(modelId);
    },
  });
};

export const useUpdateModelVersion = (options?: any) => {
  return useMutation<void, Error, ApiUpdateModelVersionRequest>({
    ...options,
    mutationFn: (request: ApiUpdateModelVersionRequest) => modelsClient.updateModelVersion(request.modelId!, request),
  });
};

export const useModelFamilies = (options?: any) => {
  return useQuery<string[]>({
    ...options,
    queryKey: ["modelFamilies"],
    queryFn: async () => modelsClient.getModelFamilies(),
  });
};

export const useModelTypes = (options?: any) => {
  return useQuery<ModelTypeDto[]>({
    ...options,
    queryKey: ["modelTypes"],
    queryFn: async () => modelsClient.getModelTypes(),
  });
};

// ============================================================================
// Multi-Model Hooks
// ============================================================================

// ============================================================================
// Unified Scenario Node Data Hook
// ============================================================================

/**
 * Internal hook for fetching full ScenarioNodeDataDto objects.
 * DO NOT use directly - use useMultiScenarioNodeData instead.
 * 
 * Supports conditional fetching with ETags:
 * - Reads cached ETags from IndexedDB before fetching
 * - Passes ETags in request (server can return 304 Not Modified)
 * - Merges server response with cached data for unchanged models
 */
const useMultiScenarioNodeDataInternal = createMultiModelDataHook<ScenarioNodeDataDto>({
  // dataType is intentionally undefined - the generic IDB init effect expects ScenarioNodeDataDto,
  // but IDB stores split data (NodeMaterialDto[] and NodeSupplementalDto[]). 
  // The batchFetchFn handles reading/merging from IDB correctly with the split format.
  dataType: undefined,
  queryKeyPrefix: 'scenarioNodeData',
  fetchFn: async (modelId, scenarioId, signal) => {
    // Single model fetch - TODO: add etag support for single fetch if needed
    return await nodesClient.getScenarioNodeData(modelId, scenarioId, signal);
  },
  batchFetchFn: async (modelIds, scenarioId, signal) => {
    // Read cached ETags and data from IndexedDB for conditional fetching
    // Materials and supplementals are stored as serialized Maps: [nodeId, Dto][]
    const [
      cachedMaterialsEtags,
      cachedSupplementalsEtags,
      cachedNodesEtags,
      cachedMaterialsRaw,
      cachedSupplementalsRaw,
      cachedNodes,
    ] = await Promise.all([
      readModelVersions('scenarioNodeMaterials', modelIds, scenarioId),
      readModelVersions('scenarioNodeSupplementals', modelIds, scenarioId),
      readModelVersions('scenarioNodes', modelIds, scenarioId),
      readMultipleModelData<[number, NodeMaterialDto][]>('scenarioNodeMaterials', modelIds, scenarioId),
      readMultipleModelData<[number, NodeSupplementalDto][]>('scenarioNodeSupplementals', modelIds, scenarioId),
      readMultipleModelData<NodeDto[]>('scenarioNodes', modelIds, scenarioId),
    ]);

    // Convert cached Maps back to arrays for merging with server response
    // Handle both old format (NodeMaterialDto[]) and new format ([nodeId, NodeMaterialDto][])
    const cachedMaterials = new Map<string, NodeMaterialDto[]>();
    cachedMaterialsRaw.forEach((entries, modelId) => {
      if (entries && entries.length > 0) {
        // Check if it's serialized Map format (array of tuples) or plain array
        const firstEntry = entries[0];
        if (Array.isArray(firstEntry)) {
          // New format: [nodeId, NodeMaterialDto][]
          cachedMaterials.set(modelId, (entries as [number, NodeMaterialDto][]).map(([, dto]) => dto));
        } else {
          // Old format: NodeMaterialDto[] - use directly
          cachedMaterials.set(modelId, entries as unknown as NodeMaterialDto[]);
        }
      }
    });

    const cachedSupplementals = new Map<string, NodeSupplementalDto[]>();
    cachedSupplementalsRaw.forEach((entries, modelId) => {
      if (entries && entries.length > 0) {
        // Check if it's serialized Map format (array of tuples) or plain array
        const firstEntry = entries[0];
        if (Array.isArray(firstEntry)) {
          // New format: [nodeId, NodeSupplementalDto][]
          cachedSupplementals.set(modelId, (entries as [number, NodeSupplementalDto][]).map(([, dto]) => dto));
        } else {
          // Old format: NodeSupplementalDto[] - use directly
          cachedSupplementals.set(modelId, entries as unknown as NodeSupplementalDto[]);
        }
      }
    });

    // Convert to objects for API request
    const materialsEtagsObj = versionsMapToObject(cachedMaterialsEtags);
    const supplementalsEtagsObj = versionsMapToObject(cachedSupplementalsEtags);
    const nodesEtagsObj = versionsMapToObject(cachedNodesEtags);

    // Log for debugging (can be removed later)
    if (Object.keys(materialsEtagsObj).length > 0 || Object.keys(nodesEtagsObj).length > 0) {
      console.log('[useMultiScenarioNodeData] Sending cached ETags:', {
        materials: materialsEtagsObj,
        supplementals: supplementalsEtagsObj,
        nodes: nodesEtagsObj,
      });
    }

    // TODO: Once server supports conditional fetching, pass etags in request:
    // new GetScenarioNodeDataBatchRequest({ 
    //   modelIds, 
    //   scenarioId,
    //   cachedNodeMaterialsEtags: materialsEtagsObj,
    //   cachedNodeSupplementalsEtags: supplementalsEtagsObj,
    //   cachedNodesEtags: nodesEtagsObj,
    // })
    const response = await nodesClient.getScenarioNodeDataBatch(
      new GetScenarioNodeDataBatchRequest({ modelIds, scenarioId }),
      signal
    );

    // Extract data from response
    const nodeMaterialsMap = response.nodeMaterials ?? {};
    const nodeSupplementalsMap = response.nodeSupplementals ?? {};
    const nodesMap = response.nodes ?? {};

    // Build result, merging server response with cached data for unchanged models
    const result: Record<string, ScenarioNodeDataDto> = {};

    for (const modelId of modelIds) {
      // Use server data if provided, otherwise fall back to cached data
      const serverNodeMaterials = nodeMaterialsMap[modelId];
      const serverNodeSupplementals = nodeSupplementalsMap[modelId];
      const serverNodes = nodesMap[modelId];

      // If server returned data for this model, use it
      // If not (304 Not Modified scenario), use cached data
      const nodeMaterials = serverNodeMaterials ?? cachedMaterials.get(modelId) ?? [];
      const nodeSupplementals = serverNodeSupplementals ?? cachedSupplementals.get(modelId) ?? [];
      const nodes = serverNodes ?? cachedNodes.get(modelId) ?? [];

      result[modelId] = new ScenarioNodeDataDto({
        nodes,
        nodeMaterials,
        nodeSupplementals,
      });
    }

    return result;
  },
  requiresScenarioId: true,
  staleTime: 5 * 60 * 1000,
  cacheTime: 30 * 60 * 1000,
  batchDebounceMs: 150,
});

/**
 * Result type for useMultiScenarioNodeData hook.
 */
export interface MultiScenarioNodeDataResult {
  /** Nodes (NodeType=6 supplemental nodes) by modelId */
  nodes: Record<string, NodeDto[] | null>;
  nodeMaterials: Record<string, NodeMaterialDto[] | null>;
  nodeSupplementals: Record<string, NodeSupplementalDto[] | null>;
  /** 
   * Materials as a Map for O(1) lookup by nodeId.
   * Merged across all models - use this instead of building your own lookup.
   */
  nodeMaterialsMap: Map<number, NodeMaterialDto>;
  /** 
   * Supplementals as a Map for O(1) lookup by nodeId.
   * Merged across all models - use this instead of building your own lookup.
   */
  nodeSupplementalsMap: Map<number, NodeSupplementalDto>;
  /** ETags for nodes by modelId */
  nodesEtags: Record<string, string>;
  /** ETags for materials by modelId */
  nodeMaterialsEtags: Record<string, string>;
  /** ETags for supplementals by modelId */
  nodeSupplementalsEtags: Record<string, string>;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  errors: (Error | null)[];
  refetchAll: () => Promise<void>;
}

/**
 * Hook for fetching both materials and supplementals for multiple models in a single API call.
 * 
 * This hook uses createMultiModelDataHook internally for batch fetching, request cancellation,
 * and debouncing. It automatically splits the response and caches materials/supplementals
 * separately in IndexedDB for TreeBuilderWorker access.
 * 
 * Features:
 * - 50% fewer API calls (fetches both materials and supplementals in one request)
 * - Preserves batch fetching, debouncing, and request cancellation
 * - Maintains separate IDB caches for materials and supplementals
 * - Supports conditional fetching with ETags (when server supports it)
 * 
 * @param modelIds - Array of model IDs to fetch data for
 * @param scenarioId - Scenario ID (required)
 * @param options - Hook options (enabled)
 */
export function useMultiScenarioNodeData(
  modelIds: string[],
  scenarioId?: string,
  options?: UseMultiModelDataOptions
): MultiScenarioNodeDataResult {
  const queryClientHook = useQueryClient();

  // Track ETags returned from server (for future conditional fetching)
  const nodesEtagsRef = useRef<Record<string, string>>({});
  const nodeMaterialsEtagsRef = useRef<Record<string, string>>({});
  const nodeSupplementalsEtagsRef = useRef<Record<string, string>>({});

  // Use internal hook to fetch full ScenarioNodeDataDto
  const internalResult = useMultiScenarioNodeDataInternal(modelIds, scenarioId, options);

  // When data is available, write to IDB every time (no ref check).
  // Covers manual assignment (same count, different data), Rebar/Non-modeled (count change), and refetch/restore.
  useEffect(() => {
    if (!internalResult.isSuccess || !scenarioId) return;

    const nodesToWrite: Array<{ modelId: string; scenarioId?: string; data: NodeDto[]; }> = [];
    const nodeMaterialsToWrite: Array<{ modelId: string; scenarioId?: string; data: [number, INodeMaterialDto][]; }> = [];
    const nodeSupplementalsToWrite: Array<{ modelId: string; scenarioId?: string; data: [number, INodeSupplementalDto][]; }> = [];

    Object.entries(internalResult.data).forEach(([modelId, nodeData]) => {
      if (!nodeData) return;

      // Update React Query caches (for backward compatibility)
      if (nodeData.nodes && nodeData.nodes.length > 0) {
        queryClientHook.setQueryData(
          ['scenarioNodes', modelId, scenarioId],
          nodeData.nodes
        );
        nodesToWrite.push({
          modelId,
          scenarioId: scenarioId,
          data: nodeData.nodes,
        });
      }

      if (nodeData.nodeMaterials && nodeData.nodeMaterials.length > 0) {
        queryClientHook.setQueryData(
          ['scenarioNodeMaterials', modelId, scenarioId],
          nodeData.nodeMaterials
        );
        const materialsMap = materialsArrayToMap(nodeData.nodeMaterials);
        nodeMaterialsToWrite.push({
          modelId,
          scenarioId: scenarioId,
          data: serializeMap(materialsMap),
        });
      }

      if (nodeData.nodeSupplementals && nodeData.nodeSupplementals.length > 0) {
        queryClientHook.setQueryData(
          ['scenarioNodeSupplementals', modelId, scenarioId],
          nodeData.nodeSupplementals
        );
        const supplementalsMap = supplementalsArrayToMap(nodeData.nodeSupplementals);
        nodeSupplementalsToWrite.push({
          modelId,
          scenarioId: scenarioId,
          data: serializeMap(supplementalsMap),
        });
      }
    });

    if (nodesToWrite.length > 0) {
      console.log(`[useMultiScenarioNodeData] Writing ${nodesToWrite.length} model nodes to IDB`);
      writeMultipleModelData('scenarioNodes', nodesToWrite).catch((err) =>
        console.warn('[useMultiScenarioNodeData] Error writing nodes to IDB:', err)
      );
    }
    if (nodeMaterialsToWrite.length > 0) {
      console.log(`[useMultiScenarioNodeData] Writing ${nodeMaterialsToWrite.length} model materials to IDB (as Maps)`);
      writeMultipleModelData('scenarioNodeMaterials', nodeMaterialsToWrite).catch((err) =>
        console.warn('[useMultiScenarioNodeData] Error writing materials to IDB:', err)
      );
    }
    if (nodeSupplementalsToWrite.length > 0) {
      console.log(`[useMultiScenarioNodeData] Writing ${nodeSupplementalsToWrite.length} model supplementals to IDB (as Maps)`);
      writeMultipleModelData('scenarioNodeSupplementals', nodeSupplementalsToWrite).catch((err) =>
        console.warn('[useMultiScenarioNodeData] Error writing supplementals to IDB:', err)
      );
    }
  }, [internalResult.isSuccess, internalResult.data, queryClientHook, scenarioId]);

  // Transform data into split format (arrays by modelId)
  const nodes = useMemo(() => {
    const result: Record<string, NodeDto[] | null> = {};
    Object.entries(internalResult.data).forEach(([modelId, nodeData]) => {
      result[modelId] = nodeData?.nodes ?? null;
    });
    return result;
  }, [internalResult.data]);

  const materials = useMemo(() => {
    const result: Record<string, NodeMaterialDto[] | null> = {};
    Object.entries(internalResult.data).forEach(([modelId, nodeData]) => {
      result[modelId] = nodeData?.nodeMaterials ?? null;
    });
    return result;
  }, [internalResult.data]);

  const supplementals = useMemo(() => {
    const result: Record<string, NodeSupplementalDto[] | null> = {};
    Object.entries(internalResult.data).forEach(([modelId, nodeData]) => {
      result[modelId] = nodeData?.nodeSupplementals ?? null;
    });
    return result;
  }, [internalResult.data]);

  // Compute merged Maps for O(1) lookup across all models
  // These replace the useMemo Map-building in MuiAssignTab
  const materialsMap = useMemo(() => {
    const map = new Map<number, NodeMaterialDto>();
    Object.values(internalResult.data).forEach((nodeData) => {
      if (nodeData?.nodeMaterials) {
        for (const material of nodeData.nodeMaterials) {
          if (material.nodeId != null) {
            map.set(material.nodeId, material);
          }
        }
      }
    });
    return map;
  }, [internalResult.data]);

  const supplementalsMap = useMemo(() => {
    const map = new Map<number, NodeSupplementalDto>();
    Object.values(internalResult.data).forEach((nodeData) => {
      if (nodeData?.nodeSupplementals) {
        for (const supp of nodeData.nodeSupplementals) {
          if (supp.nodeId != null) {
            map.set(supp.nodeId, supp);
          }
        }
      }
    });
    return map;
  }, [internalResult.data]);

  return {
    nodes: nodes,
    nodeMaterials: materials,
    nodeSupplementals: supplementals,
    nodeMaterialsMap: materialsMap,
    nodeSupplementalsMap: supplementalsMap,
    nodesEtags: nodesEtagsRef.current,
    nodeMaterialsEtags: nodeMaterialsEtagsRef.current,
    nodeSupplementalsEtags: nodeSupplementalsEtagsRef.current,
    isLoading: internalResult.isLoading,
    isFetching: internalResult.isFetching,
    isSuccess: internalResult.isSuccess,
    errors: internalResult.errors,
    refetchAll: internalResult.refetchAll,
  };
}

// ============================================================================
// Legacy Hooks (Deprecated)
// ============================================================================

/**
 * @deprecated Use useMultiScenarioNodeData instead - fetches both materials and supplementals in one call.
 * 
 * Hook for fetching NodeMaterialDto[] for multiple models.
 * Requires scenarioId. Data is cached per model+scenario combination.
 *
 * @param modelIds - Array of model IDs to fetch data for
 * @param scenarioId - Scenario ID (required for this data type)
 * @param options - Hook options (enabled)
 */
export const useMultiScenarioNodeMaterials = createMultiModelDataHook<NodeMaterialDto[]>({
  dataType: 'scenarioNodeMaterials',
  queryKeyPrefix: 'scenarioNodeMaterials',
  fetchFn: async (modelId, scenarioId, signal) => {
    const data = await nodesClient.getScenarioNodeData(modelId, scenarioId, signal);
    return data?.nodeMaterials ?? null;
  },
  batchFetchFn: async (modelIds, scenarioId, signal) => {
    const response = await nodesClient.getScenarioNodeDataBatch(
      new GetScenarioNodeDataBatchRequest({ modelIds, scenarioId }),
      signal
    );
    return response.nodeMaterials ?? {};
  },
  requiresScenarioId: true,
});

/**
 * @deprecated Use useMultiScenarioNodeData instead - fetches both materials and supplementals in one call.
 * 
 * Hook for fetching NodeSupplementalDto[] for multiple models.
 * Requires scenarioId. Data is cached per model+scenario combination.
 *
 * @param modelIds - Array of model IDs to fetch data for
 * @param scenarioId - Scenario ID (required for this data type)
 * @param options - Hook options (enabled)
 */
export const useMultiScenarioNodeSupplementals = createMultiModelDataHook<NodeSupplementalDto[]>({
  dataType: 'scenarioNodeSupplementals',
  queryKeyPrefix: 'scenarioNodeSupplementals',
  fetchFn: async (modelId, scenarioId, signal) => {
    const data = await nodesClient.getScenarioNodeData(modelId, scenarioId, signal);
    return data?.nodeSupplementals ?? null;
  },
  batchFetchFn: async (modelIds, scenarioId, signal) => {
    const response = await nodesClient.getScenarioNodeDataBatch(
      new GetScenarioNodeDataBatchRequest({ modelIds, scenarioId }),
      signal
    );
    return response.nodeSupplementals ?? {};
  },
  requiresScenarioId: true,
});

// Re-export types and utilities for consumers
export type { MultiModelDataResult, UseMultiModelDataOptions };

// Re-export DTOs for convenience
  export type { NodeDto, NodeMaterialDto, NodeSupplementalDto, ScenarioNodeDataDto };

